import * as Sentry from '@sentry/react'
import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router-dom'
import GooglePlayAndAppStoreBadgeLinks from '../../components/GooglePlayAndAppStoreBadgeLinks'
import PageLayout from '../../components/PageLayout'
import SubscriptionDialog from '../../components/SubscriptionDialog'
import { useAppConfig } from '../../contexts/AppConfigContext'
import isLifetimeSubscription from '../../helpers/isLifetimeSubscription'
import localizeDate from '../../helpers/localizeDate'
import { generateMagicLink } from '../../helpers/supabase'
import useDeviceType from '../../hooks/useDeviceType'
import { GetSubscriptionsLoaderData } from '../../loaders/getSubscriptionsLoader'

const inAppBrowserPattern = /Instagram|FBAN|FBAV|FB_IAB|Line|MicroMessenger|wv/i

export default function SubscriptionPage() {
  const { logo } = useAppConfig()
  const { subscriptions, userEmail } =
    useLoaderData() as GetSubscriptionsLoaderData
  const [displayEmail] = useState(userEmail)
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false)

  const { t } = useTranslation()
  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL
  const appName = import.meta.env.VITE_APP_NAME as string

  const isInAppBrowser = useMemo(
    () => inAppBrowserPattern.test(navigator.userAgent),
    [],
  )
  const device = useDeviceType()
  const appleAppStoreUrl = import.meta.env.VITE_APP_URL_APPLE_APP_STORE as
    | string
    | undefined
  const googlePlayStoreUrl = import.meta.env.VITE_APP_URL_GOOGLE_PLAY_STORE as
    | string
    | undefined
  const webAppUrl = import.meta.env.VITE_APP_URL_WEB as string | undefined

  const hasStoreUrls = !!(appleAppStoreUrl || googlePlayStoreUrl)
  const hasWebApp = !!webAppUrl
  const hasDownloadDestination = hasStoreUrls || hasWebApp
  // The QR code only helps hop the visitor from desktop to their phone to
  // install a native app. When only the web app is available it opens right in
  // the browser via the button below, so a QR adds friction without value.
  const showQrCode = device === 'desktop' && hasStoreUrls

  useEffect(() => {
    if (!hasDownloadDestination) {
      Sentry.captureMessage(
        'SubscriptionPage rendered without any download destination: set VITE_APP_URL_APPLE_APP_STORE, VITE_APP_URL_GOOGLE_PLAY_STORE, or VITE_APP_URL_WEB.',
        'error',
      )
    }
  }, [hasDownloadDestination])

  const step1Body = (() => {
    if (device === 'desktop') {
      if (hasStoreUrls) {
        return t('pages.account.subscription.step1BodyDesktop', {
          appName,
          defaultValue:
            'Scan the QR code with your phone to download {{appName}}, or search for {{appName}} on the App Store or Google Play.',
        })
      }
      if (hasWebApp) {
        return t('pages.account.subscription.step1BodyDesktopWeb', {
          appName,
          defaultValue:
            'Use the button below to open {{appName}} in your browser.',
        })
      }
    } else {
      if (hasStoreUrls) {
        return t('pages.account.subscription.step1Body', {
          appName,
          defaultValue: 'Tap the button below to download {{appName}}.',
        })
      }
      if (hasWebApp) {
        return t('pages.account.subscription.step1BodyWeb', {
          appName,
          defaultValue:
            'Tap the button below to open {{appName}} in your browser.',
        })
      }
    }
    return t('pages.account.subscription.step1Unavailable', {
      defaultValue:
        'The app is not available for download just yet. Reach out to us below and we will help you get started.',
    })
  })()

  const activeSubscription = subscriptions.find((s) => s.isActive)
  const isLifetime = activeSubscription
    ? isLifetimeSubscription(activeSubscription)
    : false
  const [emailCopied, setEmailCopied] = useState(false)

  const copyEmail = useCallback(() => {
    navigator.clipboard.writeText(displayEmail).then(() => {
      setEmailCopied(true)
      setTimeout(() => setEmailCopied(false), 2000)
    })
  }, [displayEmail])

  const [isOpeningWebApp, setIsOpeningWebApp] = useState(false)

  const handleOpenWebApp = useCallback(async () => {
    if (!webAppUrl) return
    setIsOpeningWebApp(true)

    const openedWindow = window.open('about:blank', '_blank')
    if (!openedWindow) {
      window.open(webAppUrl, '_blank')
      setIsOpeningWebApp(false)
      return
    }
    openedWindow.opener = null

    try {
      const { magicLinkUrl } = await generateMagicLink({
        redirectTo: webAppUrl,
      })
      openedWindow.location.href = magicLinkUrl
    } catch {
      openedWindow.location.href = webAppUrl
    } finally {
      setIsOpeningWebApp(false)
    }
  }, [webAppUrl])

  const openWebAppButton = webAppUrl ? (
    <div className="flex justify-center">
      <button
        className="inline-flex items-center gap-2 rounded-xl bg-black px-5 py-3 text-sm font-bold text-white hover:opacity-90 disabled:opacity-70"
        disabled={isOpeningWebApp}
        onClick={handleOpenWebApp}
        type="button"
      >
        <svg
          aria-hidden="true"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3M3.6 9h16.8M3.6 15h16.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {t('pages.account.subscription.openInBrowser', {
          defaultValue: 'Open in your browser',
        })}
      </button>
    </div>
  ) : null

  return (
    <>
      <PageLayout>
        <div className="flex shrink-0 items-center justify-between">
          {logo}

          {subscriptions.length > 0 && (
            <button
              className="p-4 underline hover:opacity-80"
              onClick={() => setOpenSubscriptionDialog(true)}
              type="button"
              data-testid="button-info"
            >
              {t('pages.account.subscription.subscriptionInfo', {
                defaultValue: 'Subscription Info',
              })}
            </button>
          )}
        </div>

        <div className="flex grow flex-col items-center space-y-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold sm:text-3xl">
              {t('pages.account.subscription.heading', {
                appName,
                defaultValue: 'Welcome to {{appName}}!',
              })}
            </h1>
            <p className="text-custom-light-gray mt-2 text-sm">
              {t('pages.account.subscription.subheading', {
                appName,
                defaultValue:
                  "Congratulations and thank you so much for joining {{appName}}! Your subscription is active and you're all set to start exploring.",
              })}
            </p>
          </div>

          {activeSubscription && (
            <div className="w-full max-w-md rounded-2xl border border-black/10 bg-black/3 p-5 text-center">
              <p className="text-primary mb-3 text-sm font-semibold">
                <span className="bg-primary mr-1.5 inline-block h-2 w-2 rounded-full" />
                {t('pages.account.subscription.statusActive', {
                  defaultValue: 'Your subscription is active',
                })}
              </p>
              <div className="flex justify-center gap-10 text-sm">
                <div>
                  <p className="text-custom-light-gray">
                    {t('pages.account.subscription.started', {
                      defaultValue: 'Started',
                    })}
                  </p>
                  <p className="mt-0.5 font-bold">
                    {localizeDate(activeSubscription.purchaseDate)}
                  </p>
                </div>
                {activeSubscription.expiresDate && !isLifetime && (
                  <div>
                    <p className="text-custom-light-gray">
                      {t(
                        `pages.account.subscription.${activeSubscription.willRenew ? 'renewsOn' : 'endsOn'}`,
                        {
                          defaultValue: activeSubscription.willRenew
                            ? 'Renews on'
                            : 'Ends on',
                        },
                      )}
                    </p>
                    <p className="mt-0.5 font-bold">
                      {localizeDate(activeSubscription.expiresDate)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="w-full max-w-md space-y-4">
            <h2 className="text-center text-lg font-bold">
              {t('pages.account.subscription.stepsHeading', {
                defaultValue: 'Get started in 3 easy steps',
              })}
            </h2>

            <div className="rounded-2xl border border-black/10 bg-black/3 p-5">
              <div className="mb-2 flex items-start gap-3">
                <span className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                  1
                </span>
                <div>
                  <p className="font-bold">
                    {hasWebApp && !hasStoreUrls
                      ? t('pages.account.subscription.step1TitleWeb', {
                          defaultValue: 'Open the app',
                        })
                      : t('pages.account.subscription.step1Title', {
                          defaultValue: 'Download the app',
                        })}
                  </p>
                  <p className="text-custom-light-gray mt-1 text-sm">
                    {step1Body}
                  </p>
                </div>
              </div>

              {(showQrCode || hasStoreUrls) && (
                <div className="mt-4 flex flex-col items-center gap-4">
                  {showQrCode && (
                    <div className="rounded-xl bg-white p-3">
                      <QRCodeSVG
                        size={140}
                        value={`${window.location.origin}/account/download`}
                      />
                    </div>
                  )}
                  {hasStoreUrls && (
                    <GooglePlayAndAppStoreBadgeLinks
                      preferAndroidFirst={device === 'android'}
                    />
                  )}
                </div>
              )}

              {hasWebApp && (
                <div className="mt-4">
                  {hasStoreUrls && (
                    <p className="text-custom-light-gray mb-3 text-center text-sm">
                      {t('pages.account.subscription.orOpenInBrowserTitle', {
                        defaultValue: 'Or open it directly in your browser',
                      })}
                    </p>
                  )}
                  {openWebAppButton}
                </div>
              )}

              {isInAppBrowser && (
                <p className="text-custom-light-gray mt-3 text-center text-xs">
                  {t('pages.account.subscription.inAppBrowserHint', {
                    defaultValue:
                      'If links do not open properly, tap the menu in the top-right corner and choose "Open in browser" or "Open in Safari/Chrome".',
                  })}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-black/10 bg-black/3 p-5">
              <div className="flex items-start gap-3">
                <span className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                  2
                </span>
                <div>
                  <p className="font-bold">
                    {t('pages.account.subscription.step2Title', {
                      defaultValue: 'Log in with your account',
                    })}
                  </p>
                  <p className="text-custom-light-gray mt-1 text-sm">
                    {t('pages.account.subscription.step2Body', {
                      defaultValue:
                        'Open the app, tap Log In, and sign in using the same email and password you just created here:',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={copyEmail}
                    className="mt-2 flex items-center gap-1.5 text-left text-sm font-bold break-all hover:opacity-70"
                  >
                    <span data-csr-mask>{displayEmail}</span>
                    <span className="text-custom-light-gray shrink-0 text-xs font-normal">
                      {emailCopied
                        ? t('shared.copied', { defaultValue: 'Copied!' })
                        : t('shared.tapToCopy', {
                            defaultValue: 'Tap to copy',
                          })}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-black/10 bg-black/3 p-5">
              <div className="flex items-start gap-3">
                <span className="bg-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                  3
                </span>
                <div>
                  <p className="font-bold">
                    {t('pages.account.subscription.step3Title', {
                      defaultValue: 'Start exploring',
                    })}
                  </p>
                  <p className="text-custom-light-gray mt-1 text-sm">
                    {t('pages.account.subscription.step3Body', {
                      defaultValue:
                        "That's it! Everything is unlocked and ready. Dive in and have fun with it!",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-primary border-primary/30 bg-primary/10 w-full max-w-md rounded-2xl px-5 py-4 text-center text-sm font-medium">
            {t('pages.account.subscription.noNewSubscription', {
              appName,
              defaultValue:
                'You already have an active subscription — do not start a new one through the app. Just log in and everything will be there.',
            })}
          </div>

          {supportEmail && (
            <div className="text-center">
              <p className="font-bold">
                {t('pages.account.subscription.supportHeading', {
                  defaultValue: "We're here for you",
                })}
              </p>
              <p className="text-custom-light-gray mt-1 text-sm">
                {t('pages.account.subscription.supportBody', {
                  defaultValue:
                    'If you have any questions or need help getting started, our team is happy to assist.',
                })}
              </p>
              <a
                href={`mailto:${supportEmail}`}
                className="mt-2 inline-block text-sm font-medium underline hover:opacity-70"
              >
                {supportEmail}
              </a>
            </div>
          )}
        </div>
      </PageLayout>

      {openSubscriptionDialog && subscriptions.length > 0 && (
        <SubscriptionDialog
          onClose={() => setOpenSubscriptionDialog(false)}
          open={openSubscriptionDialog}
          subscriptions={subscriptions}
        />
      )}
    </>
  )
}
